/**
 * FileDropzone — composant générique réutilisable de drop-zone multi-fichiers.
 *
 * Origine : lifté depuis `pages/public-intake/_parts/PublicFileDropzone.tsx`
 * (chunk 9.N.2 v2) et paramétré côté i18n pour permettre la réutilisation par
 * d'autres surfaces (broker intake upload 9.T.3, futures surfaces upload).
 *
 * Deux états visuels (identiques à l'original Apple-like brand-aligned) :
 *   - Empty (idle) : grande zone dashed avec cloud-up SVG brand-600 + bouton
 *     "Parcourir" outline. Drag-over → bg brand-50/60 + border solid brand-400
 *     + scale 1.005 (motion-safe).
 *   - Filled : la zone dashed devient une fine barre "+ Ajouter d'autres
 *     fichiers" et la liste de fichiers prend la place visuelle.
 *
 * i18n parameterization (9.T.3) :
 *   La prop `i18nNamespace` (par défaut `"public_intake.form.files"`) définit
 *   la racine sous laquelle le composant lit ses clés. Les clés attendues
 *   sous ce namespace :
 *     - `label`
 *     - `drag_over`
 *     - `drop_or_click`
 *     - `picker_button`
 *     - `add_more`
 *     - `selected_count` (interp `{count}`)
 *     - `limits_hint` (interp `{maxFiles, maxSizePerFileMb, maxTotalMb}`)
 *     - `accepted_types_hint`
 *     - `remove`
 *     - `errors.too_many` (interp `{max}`)
 *     - `errors.size_per_file` (interp `{maxMb}`)
 *     - `errors.total_size` (interp `{maxMb}`)
 *     - `errors.mime_rejected`
 *
 * Validations client-side UX-only (backend reste source de vérité) :
 *   count > 10 → toast/error
 *   file size > 25MB → toast/error sur le file index
 *   total size > 100MB → toast/error
 *   MIME hors whitelist → toast/error sur le file index
 *
 * Anti-leak FE : aucun détail technique ne fuit côté UI (pas de console.log,
 * pas de stack, pas de `details` projeté). Messages d'erreur i18n bundle.
 *
 * Architecture controlled : le parent tient l'état des fichiers ; le dropzone
 * notifie via `onFilesChange`. Un seul `<input type="file">` toujours présent
 * (off-screen) pour que `inputRef.current` soit stable entre les états.
 */

import type { PublicIntakeLimits } from "@kollectivia/shared";
import { useCallback, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

const MAX_FILES = 10;
const MAX_SIZE_PER_FILE_MB = 25;
const MAX_TOTAL_MB = 100;

const ACCEPTED_MIMES = [
  "application/pdf",
  "image/jpeg",
  "image/png",
  "image/webp",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "text/csv",
  "text/plain",
] as const;

// `accept` attribute pour file input — extensions + MIME pour OS pickers tolérants.
const ACCEPT_ATTR = [
  ".pdf",
  ".jpg,.jpeg",
  ".png",
  ".webp",
  ".docx",
  ".xlsx",
  ".csv",
  ".txt",
  ...ACCEPTED_MIMES,
].join(",");

export type FileDropzoneProps = {
  readonly limits?: PublicIntakeLimits;
  readonly acceptedTypesHint?: string;
  readonly files: readonly File[];
  readonly onFilesChange: (files: readonly File[]) => void;
  readonly disabled?: boolean;
  readonly errorMessage?: string;
  /**
   * Racine i18n sous laquelle lire les libellés. Default = compat
   * `"public_intake.form.files"` (9.N.2 v2). 9.T.3 broker intake passe
   * `"intake_events.upload.files"`.
   */
  readonly i18nNamespace?: string;
};

type LocalError =
  | { readonly kind: "too_many" }
  | { readonly kind: "size_per_file"; readonly fileName: string }
  | { readonly kind: "total_size" }
  | { readonly kind: "mime_rejected"; readonly fileName: string }
  | null;

function formatSize(bytes: number): string {
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(0)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}

export function FileDropzone({
  files,
  limits,
  acceptedTypesHint,
  onFilesChange,
  disabled = false,
  errorMessage,
  i18nNamespace = "public_intake.form.files",
}: FileDropzoneProps): JSX.Element {
  const { t } = useTranslation();
  const maxFiles = limits?.max_files ?? MAX_FILES;
  const maxFileMb = limits?.max_size_mb_per_file ?? MAX_SIZE_PER_FILE_MB;
  const maxTotalMb = limits?.max_total_mb ?? MAX_TOTAL_MB;
  const acceptedMimes = limits?.mime_types ?? ACCEPTED_MIMES;

  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [localError, setLocalError] = useState<LocalError>(null);
  const labelId = useId();
  const limitsId = `${labelId}-limits`;
  const errorId = `${labelId}-error`;

  const k = (suffix: string): string => `${i18nNamespace}.${suffix}`;

  const validateAndAdd = useCallback(
    (incoming: readonly File[]): void => {
      setLocalError(null);
      const merged = [...files, ...incoming];

      if (merged.length > maxFiles) {
        setLocalError({ kind: "too_many" });
        return;
      }

      for (const f of incoming) {
        if (f.size > maxFileMb * 1024 * 1024) {
          setLocalError({ kind: "size_per_file", fileName: f.name });
          return;
        }
        // MIME check — file.type peut être vide ("") sur certains OS,
        // dans ce cas on accepte (le backend fait l'anti-spoofing magic bytes).
        if (f.type && !(acceptedMimes as readonly string[]).includes(f.type)) {
          setLocalError({ kind: "mime_rejected", fileName: f.name });
          return;
        }
      }

      const totalBytes = merged.reduce((sum, f) => sum + f.size, 0);
      if (totalBytes > maxTotalMb * 1024 * 1024) {
        setLocalError({ kind: "total_size" });
        return;
      }

      onFilesChange(merged);
    },
    [files, onFilesChange, maxFiles, maxFileMb, maxTotalMb, acceptedMimes],
  );

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const list = e.target.files;
    if (!list || list.length === 0) return;
    validateAndAdd(Array.from(list));
    e.target.value = "";
  };

  const onDrop = (e: React.DragEvent<HTMLElement>): void => {
    e.preventDefault();
    setDragOver(false);
    if (disabled) return;
    const list = e.dataTransfer.files;
    if (!list || list.length === 0) return;
    validateAndAdd(Array.from(list));
  };

  const onDragOver = (e: React.DragEvent<HTMLElement>): void => {
    e.preventDefault();
    if (!disabled) setDragOver(true);
  };

  const onDragLeave = (): void => setDragOver(false);

  const removeFile = (index: number): void => {
    setLocalError(null);
    onFilesChange(files.filter((_, i) => i !== index));
  };

  const localErrorMessage: string | null = (() => {
    if (!localError) return null;
    if (localError.kind === "too_many") {
      return t(k("errors.too_many"), { max: maxFiles });
    }
    if (localError.kind === "size_per_file") {
      return `${localError.fileName} — ${t(k("errors.size_per_file"), { maxMb: maxFileMb })}`;
    }
    if (localError.kind === "total_size") {
      return t(k("errors.total_size"), { maxMb: maxTotalMb });
    }
    if (localError.kind === "mime_rejected") {
      return `${localError.fileName} — ${t(k("errors.mime_rejected"))}`;
    }
    return null;
  })();

  const displayedError = errorMessage ?? localErrorMessage;
  const totalBytes = files.reduce((sum, f) => sum + f.size, 0);
  const isEmpty = files.length === 0;
  const limitReached = files.length >= maxFiles;

  return (
    <div className="space-y-3">
      <label id={labelId} className="sr-only">
        {t(k("label"))}
      </label>

      <input
        ref={inputRef}
        type="file"
        multiple
        accept={limits ? acceptedMimes.join(",") : ACCEPT_ATTR}
        onChange={onInputChange}
        disabled={disabled || limitReached}
        className="sr-only"
        aria-hidden="true"
        tabIndex={-1}
      />

      {isEmpty ? (
        <div
          role="region"
          aria-labelledby={labelId}
          aria-describedby={displayedError ? errorId : limitsId}
          aria-invalid={displayedError ? "true" : undefined}
          className={[
            "relative rounded-2xl border-2 border-dashed p-7 text-center transition-[background-color,border-color,transform] duration-200 motion-reduce:transition-none",
            dragOver
              ? "border-brand-400 bg-brand-50/60 scale-[1.005] border-solid"
              : "border-slate-300 bg-slate-50/60",
            disabled ? "opacity-60" : "",
            displayedError ? "border-red-300 bg-red-50/60" : "",
          ].join(" ")}
          onDrop={onDrop}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
        >
          <div className="flex flex-col items-center gap-3">
            <CloudUpIcon aria-hidden="true" className="text-brand-600 h-9 w-9" />
            <p className="text-sm font-semibold text-slate-800">
              {dragOver ? t(k("drag_over")) : t(k("drop_or_click"))}
            </p>
            <button
              type="button"
              disabled={disabled}
              onClick={() => inputRef.current?.click()}
              className="focus-visible:ring-brand-500 touch-manipulation rounded-lg border border-slate-300 bg-white px-3.5 py-1.5 text-sm font-medium text-slate-800 shadow-sm transition-colors hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 motion-reduce:transition-none"
            >
              {t(k("picker_button"))}
            </button>
          </div>
          {dragOver && (
            <p className="sr-only" aria-live="polite">
              {t(k("drag_over"))}
            </p>
          )}
        </div>
      ) : (
        <div className="space-y-2.5">
          <ul className="overflow-hidden rounded-xl border border-slate-200 bg-white">
            {files.map((f, i) => (
              <li
                key={`${f.name}-${i}`}
                style={{
                  animationDelay: `${Math.min(i, 5) * 30}ms`,
                }}
                className="motion-safe:animate-fade-in flex items-center gap-3 border-b border-slate-100 px-3 py-2.5 last:border-b-0"
              >
                <FileIcon aria-hidden="true" className="h-5 w-5 flex-shrink-0 text-slate-400" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-slate-800" title={f.name}>
                    {f.name}
                  </p>
                  <p className="text-xs tabular-nums text-slate-500">{formatSize(f.size)}</p>
                </div>
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => removeFile(i)}
                  aria-label={`${t(k("remove"))} ${f.name}`}
                  className="focus-visible:ring-brand-500 inline-flex h-8 w-8 flex-shrink-0 touch-manipulation items-center justify-center rounded-md text-slate-400 transition-colors hover:bg-slate-50 hover:text-red-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 motion-reduce:transition-none"
                >
                  <XIcon aria-hidden="true" className="h-4 w-4" />
                </button>
              </li>
            ))}
          </ul>

          <button
            type="button"
            disabled={disabled || limitReached}
            onClick={() => inputRef.current?.click()}
            onDrop={onDrop}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            className="hover:border-brand-400 hover:bg-brand-50/30 focus-visible:ring-brand-500 inline-flex w-full touch-manipulation items-center justify-center gap-1.5 rounded-lg border border-dashed border-slate-300 bg-white px-3 py-2.5 text-sm font-medium text-slate-700 transition-colors hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 motion-reduce:transition-none"
          >
            <PlusIcon aria-hidden="true" className="h-3.5 w-3.5" />
            {t(k("add_more"))}
          </button>

          <p className="text-xs tabular-nums text-slate-500" aria-live="polite">
            {t(k("selected_count"), { count: files.length })}
            {" · "}
            {formatSize(totalBytes)}
          </p>
        </div>
      )}

      <p id={limitsId} className="text-xs text-slate-500">
        {t(k("limits_hint"), {
          count: maxFiles,
          maxFiles: maxFiles,
          maxSizePerFileMb: maxFileMb,
          maxTotalMb: maxTotalMb,
        })}
      </p>
      <p className="text-xs text-slate-500">{acceptedTypesHint ?? t(k("accepted_types_hint"))}</p>

      {displayedError && (
        <p
          id={errorId}
          role="alert"
          aria-live="polite"
          className="text-xs font-medium text-red-700"
        >
          {displayedError}
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Icônes inline — vectorielles, mono, pas de raster, pas de dépendance.
// ---------------------------------------------------------------------------

function CloudUpIcon(props: React.SVGProps<SVGSVGElement>): JSX.Element {
  return (
    <svg viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
      <path
        d="M11 25.5h14a6 6 0 0 0 .5-12 8 8 0 0 0-15.4-1.6A5.5 5.5 0 0 0 11 25.5z"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinejoin="round"
      />
      <path
        d="M18 21V13.5M14.5 17l3.5-3.5L21.5 17"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function FileIcon(props: React.SVGProps<SVGSVGElement>): JSX.Element {
  return (
    <svg viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
      <path
        d="M5.5 2.5h6l4 4v10a1 1 0 0 1-1 1h-9a1 1 0 0 1-1-1V3.5a1 1 0 0 1 1-1z"
        stroke="currentColor"
        strokeWidth="1.25"
        strokeLinejoin="round"
      />
      <path
        d="M11.5 2.5v4h4M7.5 11h5M7.5 13.5h5"
        stroke="currentColor"
        strokeWidth="1.25"
        strokeLinecap="round"
      />
    </svg>
  );
}

function XIcon(props: React.SVGProps<SVGSVGElement>): JSX.Element {
  return (
    <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
      <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function PlusIcon(props: React.SVGProps<SVGSVGElement>): JSX.Element {
  return (
    <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
      <path d="M8 3.5v9M3.5 8h9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}
